import { callInternal } from './internal.js';

export type EventListType = 'waiting' | 'visible' | 'created' | 'destroyed';
export type EventHandleStatus = '' | '0' | '1' | '2' | '3';

export interface EventPerson {
	user_id?: string | number;
	user_name?: string;
	real_name?: string;
	mobile?: string;
	avatar?: string;
	read_status?: string | number;
	read_time?: string | null;
	flag?: string | number;
	is_first?: string | number;
	[k: string]: unknown;
}

export interface EventPic {
	pic_id?: string | number;
	pic_url?: string;
	url?: string;
	device_auto_id?: string | number;
	channel_id?: string | number;
	device_property_id?: string | number;
	screenshot_time?: string;
	alias?: string;
	[k: string]: unknown;
}

export interface EventAudit {
	audit_id?: string | number;
	user_id?: string | number;
	real_name?: string;
	mobile?: string;
	avatar?: string;
	content?: string;
	audit_status?: string | number;
	create_time?: string;
	last_update_time?: string;
	[k: string]: unknown;
}

export interface EventComment {
	user_id?: string | number;
	real_name?: string;
	mobile?: string;
	avatar?: string;
	content?: string;
	comment_type?: string | number;
	comment_time?: string;
	data_type?: string | number;
	pic_id?: string | number | null;
	pic_url?: string | null;
	screenshot_time?: string | null;
	handle_status?: string | number;
	[k: string]: unknown;
}

export interface EventItem {
	event_id?: string | number;
	user_id?: string | number;
	real_name?: string;
	mobile?: string;
	avatar?: string;
	store_id?: string | number;
	store_name?: string;
	store_address?: string;
	shopowner?: string;
	shopowner_phone?: string | number;
	event_type?: string | number;
	event_type_name?: string;
	event_status?: string | number;
	life_status?: string | number;
	event_description?: string;
	priority?: string | number;
	exceed_time?: string;
	expiration_time?: string;
	create_time?: string;
	last_update_time?: string;
	pic_info?: EventPic[];
	property_list?: Array<{ property_id?: string | number; property_name?: string }>;
	user_list?: EventPerson[];
	handle?: EventPerson & { handle_time?: string; handle_result?: string | number; handle_conclusion?: string };
	destroy?: EventPerson & { destroy_time?: string } | null;
	score?: { get_score?: string | number; total?: string | number; type?: string | number };
	[k: string]: unknown;
}

export interface EventDetail extends EventItem {
	lock_user_id?: string | number;
	device?: {
		device_auto_id?: string | number;
		channel_id?: string | number;
		device_property_id?: string | number;
		alias?: string;
	};
	audit_list?: EventAudit[];
	other_comment_list?: EventComment[];
	appoint_comment_list?: EventComment[];
}

export interface EventCursorResult {
	ok: boolean;
	items: EventItem[];
	nextCursor: string;
	currentCursor?: string;
	currentCount?: number;
	total?: number;
	msg?: string;
}

export interface EventActionResult {
	ok: boolean;
	msg?: string;
}

export interface EventTypeOption {
	property_id?: string | number;
	property_name?: string;
	parent_property_id?: string | number;
	property_parent_id?: string | number;
	parent_id?: string | number;
	event_type_id?: string | number;
	event_type_name?: string;
	event_source_id?: string | number;
	event_source_name?: string;
	event_source?: string | number;
	title?: string;
	name?: string;
	score?: string | number;
	total?: string | number;
	template_id?: string | number;
	property_list?: EventTypeOption[];
	children?: EventTypeOption[];
	items?: EventTypeOption[];
	list?: EventTypeOption[];
	sub_list?: EventTypeOption[];
	create_time?: string;
	last_update_time?: string;
	[k: string]: unknown;
}

export interface EventTemplateOption {
	template_id?: string | number;
	template_name?: string;
	template_title?: string;
	title?: string;
	name?: string;
	event_type?: string | number;
	event_type_id?: string | number;
	[k: string]: unknown;
}

export interface EventPropertyOption {
	property_id?: string | number;
	property_name?: string;
	title?: string;
	name?: string;
	score?: string | number;
	total?: string | number;
	template_id?: string | number;
	event_type?: string | number;
	event_type_id?: string | number;
	[k: string]: unknown;
}

export interface EventPriorityOption {
	priority?: string | number;
	title?: string;
	name?: string;
	seconds?: string | number;
	[k: string]: unknown;
}

export interface AssignableEventUser {
	user_id: string | number;
	user_name?: string;
	real_name?: string;
	[k: string]: unknown;
}

export interface EventCreateUser {
	user_id?: string | number;
	user_name?: string;
	real_name?: string;
	name?: string;
	role_auto_id?: string | number;
	roles_name?: string;
	role_name?: string;
	[k: string]: unknown;
}

export interface EventCreateUserGroup {
	role_auto_id: string;
	roles_name: string;
	users: EventCreateUser[];
}

export interface EventCreatePayload {
	store_id?: string | number;
	event_type?: string | number;
	event_description?: string;
	priority?: string | number;
	pic_info?: string;
	property_ids?: string;
	template_id?: string | number;
	score?: string;
	user_ids?: string;
	concern_ids?: string;
	carbon_copy?: string;
}

function responseMsg(code: number, msg?: string): string {
	return `${msg || 'Request failed'} (code=${code})`;
}

function cleanText(value: unknown): string {
	return value === undefined || value === null ? '' : String(value).trim();
}

function finiteNumber(value: unknown): number | undefined {
	if (value === undefined || value === null || value === '') return undefined;
	const next = Number(value);
	return Number.isFinite(next) ? next : undefined;
}

function normalizeCursorMeta(data: Record<string, unknown>) {
	return {
		nextCursor: cleanText(data.next_cursor) || cleanText(data.nextCursor) || cleanText(data.next_page) || cleanText(data.nextPage),
		currentCursor: cleanText(data.current_cursor) || cleanText(data.currentCursor),
		currentCount: finiteNumber(data.current_count ?? data.currentCount ?? data.count),
		total: finiteNumber(data.total ?? data.total_count ?? data.totalCount ?? data.count),
	};
}

function looksLikeEventItem(data: Record<string, unknown>): boolean {
	return data.event_id != null
		|| data.event_type_name != null
		|| data.event_description != null
		|| data.store_name != null
		|| data.pic_info != null
		|| data.handle_status != null
		|| data.life_status != null;
}

function normalizeItems(data: unknown, depth = 0): {
	items: EventItem[];
	nextCursor: string;
	currentCursor?: string;
	currentCount?: number;
	total?: number;
} {
	if (depth > 8) return { items: [], nextCursor: '' };
	if (Array.isArray(data)) return { items: data as EventItem[], nextCursor: '' };
	if (!isObjectRecord(data)) return { items: [], nextCursor: '' };

	const meta = normalizeCursorMeta(data);
	if (looksLikeEventItem(data)) {
		return { items: [data as EventItem], ...meta };
	}

	for (const key of ['items', 'list', 'records', 'rows', 'result', 'data', 'content']) {
		if (!(key in data)) continue;
		const nested = normalizeItems(data[key], depth + 1);
		if (
			nested.items.length > 0
			|| nested.nextCursor
			|| nested.currentCursor
			|| nested.currentCount !== undefined
			|| nested.total !== undefined
		) {
			return {
				items: nested.items,
				nextCursor: nested.nextCursor || meta.nextCursor,
				currentCursor: nested.currentCursor || meta.currentCursor,
				currentCount: nested.currentCount ?? meta.currentCount,
				total: nested.total ?? meta.total,
			};
		}
	}

	return {
		items: [],
		nextCursor: meta.nextCursor,
		currentCursor: meta.currentCursor || undefined,
		currentCount: meta.currentCount,
		total: meta.total,
	};
}

function isObjectRecord(data: unknown): data is Record<string, unknown> {
	return Boolean(data) && typeof data === 'object' && !Array.isArray(data);
}

const NORMALIZE_ARRAY_KEYS = [
	'items',
	'item',
	'list',
	'lists',
	'rows',
	'records',
	'record',
	'data',
	'content',
	'result',
	'results',
	'property_list',
	'propertyList',
	'property_all',
	'propertyAll',
	'property_tree',
	'propertyTree',
	'property_tree_list',
	'propertyTreeList',
	'propertys',
	'properties',
	'property',
	'event_types',
	'eventTypes',
	'event_type_list',
	'eventTypeList',
	'children',
	'child',
	'child_list',
	'childList',
	'children_list',
	'childrenList',
	'sub_list',
	'subList',
	'sublist',
	'subs',
	'nodes',
	'tree',
	'trees',
	'tree_list',
	'treeList',
	'page',
	'cursor_list',
	'cursorList',
];

const SPECIFIC_OPTION_RECORD_KEYS = [
	'property_id',
	'propertyId',
	'property_auto_id',
	'propertyAutoId',
	'property_name',
	'propertyName',
	'event_type_id',
	'eventTypeId',
	'event_type',
	'event_type_name',
	'eventTypeName',
	'event_source_id',
	'eventSourceId',
	'event_source',
	'event_source_name',
	'eventSourceName',
	'template_id',
	'templateId',
	'template_name',
	'priority',
	'user_id',
	'userId',
	'role_auto_id',
	'roles_name',
	'store_id',
	'storeId',
	'store_name',
	'storeName',
];

const GENERIC_OPTION_RECORD_KEYS = [
	'name',
	'title',
	'label',
	'text',
	'id',
	'auto_id',
	'autoId',
];

const OPTION_RECORD_KEYS = [
	...SPECIFIC_OPTION_RECORD_KEYS,
	...GENERIC_OPTION_RECORD_KEYS,
];

const GENERIC_OPTION_UNWRAP_KEYS = [
	'data',
	'content',
	'result',
	'results',
	'records',
	'rows',
	'page',
	'property_list',
	'propertyList',
	'property_all',
	'propertyAll',
	'property_tree',
	'propertyTree',
	'property_tree_list',
	'propertyTreeList',
	'event_types',
	'eventTypes',
	'event_type_list',
	'eventTypeList',
	'items',
	'item',
	'list',
	'lists',
];

const EVENT_CREATE_TYPE_CATEGORY_ID_KEYS = [
	'category_id',
	'categoryId',
	'category_auto_id',
	'categoryAutoId',
	'category',
];

const EVENT_CREATE_TYPE_ID_KEYS = [
	...EVENT_CREATE_TYPE_CATEGORY_ID_KEYS,
	'property_id',
	'propertyId',
	'property_auto_id',
	'propertyAutoId',
	'event_type_id',
	'eventTypeId',
	'event_type',
	'event_source_id',
	'eventSourceId',
	'event_source',
];

const EVENT_CREATE_TYPE_CATEGORY_LABEL_KEYS = [
	'category_name',
	'categoryName',
	'category_name_en',
	'categoryNameEn',
	'category_name_zh_cn',
	'categoryNameZhCn',
	'category_name_zh_tw',
	'categoryNameZhTw',
	'category_name_es',
	'categoryNameEs',
	'category_name_ar',
	'categoryNameAr',
	'category_title',
	'categoryTitle',
	'category_title_en',
	'categoryTitleEn',
	'category_title_zh_cn',
	'categoryTitleZhCn',
	'category_title_zh_tw',
	'categoryTitleZhTw',
];

const EVENT_CREATE_TYPE_SPECIFIC_LABEL_KEYS = [
	...EVENT_CREATE_TYPE_CATEGORY_LABEL_KEYS,
	'property_name',
	'propertyName',
	'property_name_en',
	'propertyNameEn',
	'property_name_zh_cn',
	'propertyNameZhCn',
	'property_name_zh_tw',
	'propertyNameZhTw',
	'property_name_es',
	'propertyNameEs',
	'property_name_ar',
	'propertyNameAr',
	'property_title',
	'propertyTitle',
	'property_title_en',
	'propertyTitleEn',
	'property_title_zh_cn',
	'propertyTitleZhCn',
	'property_title_zh_tw',
	'propertyTitleZhTw',
	'event_type_name',
	'eventTypeName',
	'event_type_name_en',
	'eventTypeNameEn',
	'event_type_name_zh_cn',
	'eventTypeNameZhCn',
	'event_type_name_zh_tw',
	'eventTypeNameZhTw',
	'event_source_name',
	'eventSourceName',
	'event_source_name_en',
	'eventSourceNameEn',
	'event_source_name_zh_cn',
	'eventSourceNameZhCn',
	'event_source_name_zh_tw',
	'eventSourceNameZhTw',
];

const EVENT_CREATE_TYPE_GENERIC_LABEL_KEYS = [
	'title',
	'name',
	'label',
	'text',
];

const EVENT_CREATE_TYPE_CHILD_KEYS = [
	'data',
	'content',
	'result',
	'results',
	'records',
	'rows',
	'page',
	'property_list',
	'propertyList',
	'property_all',
	'propertyAll',
	'property_tree',
	'propertyTree',
	'property_tree_list',
	'propertyTreeList',
	'property_children',
	'propertyChildren',
	'property_child',
	'event_types',
	'eventTypes',
	'event_type_list',
	'eventTypeList',
	'items',
	'item',
	'list',
	'lists',
	'children',
	'child',
	'child_list',
	'childList',
	'children_list',
	'childrenList',
	'sub_list',
	'subList',
	'sublist',
	'subs',
	'nodes',
	'son',
	'sons',
	'childs',
];

const EVENT_CREATE_TYPE_CATEGORY_KEYS = [
	'categories',
	'category_list',
	'categoryList',
	'category_tree',
	'categoryTree',
	'category_all',
	'categoryAll',
];

const EVENT_CREATE_TYPE_CATEGORY_PROPERTY_KEYS = [
	'properties',
	'property_list',
	'propertyList',
	'property_all',
	'propertyAll',
	'property_tree',
	'propertyTree',
	'children',
	'child',
	'child_list',
	'childList',
	'items',
	'list',
];

function recordHasAnyKey(record: Record<string, unknown>, keys: string[]): boolean {
	for (const key of keys) {
		if (record[key] !== undefined && record[key] !== null && record[key] !== '') return true;
	}
	return false;
}

function recordHasUnwrapPayload(record: Record<string, unknown>, keys: string[]): boolean {
	for (const key of keys) {
		if (!(key in record)) continue;
		const value = record[key];
		if (Array.isArray(value) && value.length > 0) return true;
		const parsed = parseJsonLike(value);
		if (Array.isArray(parsed) && parsed.length > 0) return true;
		if (isObjectRecord(parsed) && Object.keys(parsed).length > 0) return true;
		if (isObjectRecord(value) && Object.keys(value).length > 0) return true;
	}
	return false;
}

function looksLikeOptionRecord(record: Record<string, unknown>): boolean {
	if (recordHasAnyKey(record, SPECIFIC_OPTION_RECORD_KEYS)) return true;
	if (recordHasAnyKey(record, GENERIC_OPTION_RECORD_KEYS)) {
		return !recordHasUnwrapPayload(record, GENERIC_OPTION_UNWRAP_KEYS);
	}
	for (const key of [
		'property_list',
		'propertyList',
		'property_all',
		'propertyAll',
		'property_tree',
		'propertyTree',
		'items',
		'list',
		'children',
		'child_list',
		'childList',
		'children_list',
		'childrenList',
		'sub_list',
		'subList',
	]) {
		if (Array.isArray(record[key]) && recordHasAnyKey(record, ['property_name', 'propertyName', 'name', 'title', 'label', 'id'])) {
			return true;
		}
	}
	return false;
}

function parseJsonLike(value: unknown): unknown | undefined {
	if (typeof value !== 'string') return undefined;
	const text = value.trim();
	if (!text || (text[0] !== '{' && text[0] !== '[')) return undefined;
	try {
		return JSON.parse(text);
	} catch {
		return undefined;
	}
}

function normalizeArray<T>(data: unknown, depth = 0): T[] {
	if (depth > 8) return [];
	const parsed = parseJsonLike(data);
	if (parsed !== undefined) return normalizeArray<T>(parsed, depth + 1);
	if (Array.isArray(data)) {
		const collected: T[] = [];
		let unwrapped = false;
		for (const item of data) {
			const parsedItem = parseJsonLike(item);
			if (parsedItem !== undefined) {
				const nested = normalizeArray<T>(parsedItem, depth + 1);
				if (nested.length > 0) {
					unwrapped = true;
					for (const nestedItem of nested) collected.push(nestedItem);
					continue;
				}
			}
			if (isObjectRecord(item) && !looksLikeOptionRecord(item)) {
				const nested = normalizeArray<T>(item, depth + 1);
				if (nested.length > 0) {
					unwrapped = true;
					for (const nestedItem of nested) collected.push(nestedItem);
					continue;
				}
			}
			collected.push(item as T);
		}
		return unwrapped ? collected : data as T[];
	}
	if (!isObjectRecord(data)) return [];
	if (looksLikeOptionRecord(data)) return [data as T];

	for (const key of NORMALIZE_ARRAY_KEYS) {
		if (!(key in data)) continue;
		const nested = normalizeArray<T>(data[key], depth + 1);
		if (nested.length > 0) return nested;
	}

	const collected: T[] = [];
	const keys = Object.keys(data);
	for (const key of keys) {
		const nested = normalizeArray<T>(data[key], depth + 1);
		for (const item of nested) collected.push(item);
	}
	return collected;
}

function responseCode(data: unknown): number | undefined {
	if (!isObjectRecord(data)) return undefined;
	const rawCode = data.code;
	if (rawCode === undefined || rawCode === null || rawCode === '') return undefined;
	const code = Number(rawCode);
	return Number.isFinite(code) ? code : undefined;
}

function isSuccessfulOptionResponse(data: unknown): boolean {
	const code = responseCode(data);
	return code === undefined || code === 0 || code === 1 || code === 200;
}

function normalizeOptionResponse<T>(data: unknown): T[] {
	if (!isObjectRecord(data)) return normalizeArray<T>(data);
	const fromData = normalizeArray<T>(data.data);
	if (fromData.length > 0) return fromData;
	return normalizeArray<T>(data);
}

function hasEventCreateTypeNodeIdentity(record: Record<string, unknown>): boolean {
	if (recordHasAnyKey(record, EVENT_CREATE_TYPE_ID_KEYS)) return true;
	if (recordHasAnyKey(record, EVENT_CREATE_TYPE_SPECIFIC_LABEL_KEYS)) return true;
	if (recordHasAnyKey(record, EVENT_CREATE_TYPE_GENERIC_LABEL_KEYS)) {
		return !recordHasUnwrapPayload(record, EVENT_CREATE_TYPE_CHILD_KEYS);
	}
	return false;
}

function isUngroupedEventCreateCategory(record: Record<string, unknown>): boolean {
	const categoryId = firstCleanValue(record, EVENT_CREATE_TYPE_CATEGORY_ID_KEYS).trim();
	if (categoryId === '0') return true;
	const categoryLabel = firstCleanValue(record, EVENT_CREATE_TYPE_CATEGORY_LABEL_KEYS).trim().toLowerCase();
	return (
		categoryLabel === '未分组'
		|| categoryLabel === 'ungrouped'
		|| categoryLabel === 'unclassified'
		|| categoryLabel === 'no group'
		|| categoryLabel === 'none'
	);
}

function eventCreateCategoryRecords(value: unknown): Array<Record<string, unknown>> {
	const parsed = parseJsonLike(value);
	const data = parsed !== undefined ? parsed : value;
	if (Array.isArray(data)) {
		const records: Array<Record<string, unknown>> = [];
		for (const item of data) {
			if (isObjectRecord(item)) records.push(item);
		}
		return records;
	}
	return isObjectRecord(data) ? [data] : [];
}

function eventCreateCategoryChildren(record: Record<string, unknown>, depth: number): EventTypeOption[] {
	const children: EventTypeOption[] = [];
	for (const key of EVENT_CREATE_TYPE_CATEGORY_PROPERTY_KEYS) {
		if (!(key in record)) continue;
		const nested = collectEventCreateTypeOptions(record[key], depth + 1);
		for (const item of nested) children.push(item);
	}
	return children;
}

function collectEventCreateTypeCategoryOptions(record: Record<string, unknown>, depth: number): EventTypeOption[] {
	const collected: EventTypeOption[] = [];
	for (const key of EVENT_CREATE_TYPE_CATEGORY_KEYS) {
		if (!(key in record)) continue;
		const categories = eventCreateCategoryRecords(record[key]);
		for (const category of categories) {
			const children = eventCreateCategoryChildren(category, depth + 1);
			if (children.length === 0) continue;
			if (isUngroupedEventCreateCategory(category)) {
				for (const child of children) collected.push(child);
				continue;
			}
			const normalized: EventTypeOption = { ...category, property_list: children };
			delete normalized.properties;
			collected.push(normalized);
		}
	}
	return collected;
}

function collectEventCreateTypeOptions(data: unknown, depth = 0): EventTypeOption[] {
	if (depth > 8) return [];
	const parsed = parseJsonLike(data);
	if (parsed !== undefined) return collectEventCreateTypeOptions(parsed, depth + 1);
	if (Array.isArray(data)) {
		const collected: EventTypeOption[] = [];
		for (const item of data) {
			const nested = collectEventCreateTypeOptions(item, depth + 1);
			for (const nestedItem of nested) collected.push(nestedItem);
		}
		return collected;
	}
	if (!isObjectRecord(data)) return [];
	const categoryOptions = collectEventCreateTypeCategoryOptions(data, depth);
	if (categoryOptions.length > 0) return categoryOptions;
	if (hasEventCreateTypeNodeIdentity(data)) return [data as EventTypeOption];

	const collected: EventTypeOption[] = [];
	for (const key of EVENT_CREATE_TYPE_CHILD_KEYS) {
		if (!(key in data)) continue;
		const nested = collectEventCreateTypeOptions(data[key], depth + 1);
		for (const item of nested) collected.push(item);
	}
	return collected;
}

async function fetchEventCreateTypeOptionList(opts: {
	query?: Record<string, string | number>;
	versions?: string[];
} = {}): Promise<EventTypeOption[]> {
	const versions = opts.versions ?? ['1', '2', '3'];
	let emptySuccess: EventTypeOption[] | undefined;
	for (const av of versions) {
		try {
			const res = await callInternal<unknown>({
				short_name: 'event',
				action: '/property/all',
				method: 'GET',
				query: opts.query,
				av,
			});
			if (isSuccessfulOptionResponse(res)) {
				const items = collectEventCreateTypeOptions(res);
				if (items.length > 0) return items;
				emptySuccess = emptySuccess ?? items;
			}
		} catch {
			// Try the next observed internal API version.
		}
	}
	return emptySuccess ?? [];
}

function firstCleanValue(record: Record<string, unknown>, keys: string[]): string {
	for (const key of keys) {
		const value = record[key];
		const text = cleanText(value);
		if (text) return text;
	}
	return '';
}

function eventCreateUserId(user: Record<string, unknown>): string {
	return firstCleanValue(user, ['user_id', 'userId', 'user_auto_id', 'userAutoId', 'id', 'auto_id']);
}

function eventCreateRoleId(record: Record<string, unknown>): string {
	return firstCleanValue(record, ['role_auto_id', 'roles_id', 'role_id', 'group_id', 'department_id', 'id']) || 'default';
}

function eventCreateRoleName(record: Record<string, unknown>): string {
	return firstCleanValue(record, ['roles_name', 'role_name', 'group_name', 'department_name', 'name', 'title']) || 'Name';
}

function hasEventCreateRole(record: Record<string, unknown>): boolean {
	return firstCleanValue(record, [
		'role_auto_id',
		'roles_id',
		'role_id',
		'group_id',
		'department_id',
		'roles_name',
		'role_name',
		'group_name',
		'department_name',
	]) !== '';
}

function eventCreateChildArrays(record: Record<string, unknown>): unknown[] {
	const children: unknown[] = [];
	for (const key of ['users', 'user_list', 'list', 'items', 'children', 'children_list', 'member_list', 'members', 'data']) {
		const value = record[key];
		if (Array.isArray(value)) children.push(value);
	}
	return children;
}

function addEventCreateUserToGroup(
	groups: Map<string, EventCreateUserGroup>,
	user: EventCreateUser,
	roleId: string,
	roleName: string,
) {
	const id = eventCreateUserId(user as Record<string, unknown>);
	if (!id) return;
	const groupKey = roleId || 'default';
	let group = groups.get(groupKey);
	if (!group) {
		group = { role_auto_id: groupKey, roles_name: roleName || 'Name', users: [] };
		groups.set(groupKey, group);
	}
	for (const existing of group.users) {
		if (eventCreateUserId(existing as Record<string, unknown>) === id) return;
	}
	group.users.push({ ...user, user_id: user.user_id ?? id, roles_name: user.roles_name ?? roleName, role_auto_id: user.role_auto_id ?? groupKey });
}

function collectEventCreateUsers(
	data: unknown,
	groups: Map<string, EventCreateUserGroup>,
	roleContext?: { id: string; name: string },
	depth = 0,
) {
	if (depth > 6) return;
	if (Array.isArray(data)) {
		for (const item of data) collectEventCreateUsers(item, groups, roleContext, depth + 1);
		return;
	}
	if (!isObjectRecord(data)) return;

	const ownUserId = eventCreateUserId(data);
	const ownRole = {
		id: roleContext?.id || eventCreateRoleId(data),
		name: roleContext?.name || eventCreateRoleName(data),
	};
	const childArrays = eventCreateChildArrays(data);

	if (ownUserId) {
		addEventCreateUserToGroup(groups, data as EventCreateUser, ownRole.id, ownRole.name);
	}

	if (childArrays.length === 0 && !ownUserId) return;
	const childRole = ownUserId
		? ownRole
		: hasEventCreateRole(data)
			? { id: eventCreateRoleId(data), name: eventCreateRoleName(data) }
			: roleContext;
	for (const children of childArrays) collectEventCreateUsers(children, groups, childRole, depth + 1);
}

function normalizeEventCreateUserGroups(data: unknown): EventCreateUserGroup[] {
	const groups = new Map<string, EventCreateUserGroup>();
	collectEventCreateUsers(data, groups);
	if (groups.size === 0 && isObjectRecord(data)) {
		for (const key of ['items', 'list', 'records', 'rows', 'result', 'content']) {
			collectEventCreateUsers(data[key], groups);
		}
	}
	return Array.from(groups.values()).filter((group) => group.users.length > 0);
}

function hasEventDetailShape(data: Record<string, unknown>): boolean {
	return data.event_id != null
		|| data.event_type_name != null
		|| data.event_description != null
		|| data.store_name != null
		|| data.create_time != null
		|| data.pic_info != null
		|| data.user_list != null
		|| data.handle != null
		|| data.score != null;
}

function normalizeEventDetail(data: unknown): EventDetail | undefined {
	if (Array.isArray(data)) {
		for (const item of data) {
			const detail = normalizeEventDetail(item);
			if (detail) return detail;
		}
		return undefined;
	}
	if (!isObjectRecord(data)) return undefined;
	if (hasEventDetailShape(data)) return data as EventDetail;
	for (const key of ['detail', 'event', 'item', 'data', 'content', 'result']) {
		const detail = normalizeEventDetail(data[key]);
		if (detail) return detail;
	}
	return undefined;
}

async function fetchEventOptionList<T>(action: string, opts: {
	query?: Record<string, string | number>;
	versions?: string[];
} = {}): Promise<T[]> {
	const versions = opts.versions ?? ['1', '2'];
	let emptySuccess: T[] | undefined;
	for (const av of versions) {
		try {
			const res = await callInternal<unknown>({
				short_name: 'event',
				action,
				method: 'GET',
				query: opts.query,
				av,
			});
			if (isSuccessfulOptionResponse(res)) {
				const items = normalizeOptionResponse<T>(res);
				if (items.length > 0) return items;
				emptySuccess = emptySuccess ?? items;
			}
		} catch {
			// Try the next observed internal API version.
		}
	}
	return emptySuccess ?? [];
}

export async function fetchEventAll(opts: {
	count?: number;
	cursor?: string;
	startTime?: string;
	endTime?: string;
	handleStatus?: Exclude<EventHandleStatus, ''>;
	storeIds?: string;
	eventTypeIds?: string;
}): Promise<EventCursorResult> {
	try {
		const query: Record<string, string | number> = {
			count: opts.count ?? 20,
			cursor: opts.cursor ?? '',
		};
		if (opts.startTime) query.start_time = opts.startTime;
		if (opts.endTime) query.end_time = opts.endTime;
		if (opts.handleStatus) query.handle_status = opts.handleStatus;
		if (opts.storeIds) query.store_ids = opts.storeIds;
		if (opts.eventTypeIds) query.event_type_ids = opts.eventTypeIds;

		const res = await callInternal({
			short_name: 'event',
			action: '/event/all',
			method: 'GET',
			query,
			av: '1',
		});
		if (Number(res.code) !== 0) {
			return { ok: false, items: [], nextCursor: '', msg: responseMsg(res.code, res.msg) };
		}
		const normalized = normalizeItems(res.data);
		return { ok: true, ...normalized };
	} catch (e) {
		return { ok: false, items: [], nextCursor: '', msg: e instanceof Error ? e.message : String(e) };
	}
}

export async function fetchEventCursor(opts: {
	type: EventListType;
	count?: number;
	cursor?: string;
	startTime?: string;
	endTime?: string;
	handleStatus?: Exclude<EventHandleStatus, ''>;
	storeIds?: string;
	eventTypeIds?: string;
}): Promise<EventCursorResult> {
	try {
		const query: Record<string, string | number> = {
			type: opts.type,
			count: opts.count ?? 20,
			cursor: opts.cursor ?? '',
		};
		if (opts.startTime) query.start_time = opts.startTime;
		if (opts.endTime) query.end_time = opts.endTime;
		if (opts.handleStatus) query.handle_status = opts.handleStatus;
		if (opts.storeIds) query.store_ids = opts.storeIds;
		if (opts.eventTypeIds) query.event_type_ids = opts.eventTypeIds;

		const res = await callInternal({
			short_name: 'event',
			action: '/event/cursor',
			method: 'GET',
			query,
			av: '2',
		});
		if (Number(res.code) !== 0) {
			return { ok: false, items: [], nextCursor: '', msg: responseMsg(res.code, res.msg) };
		}
		const normalized = normalizeItems(res.data);
		return { ok: true, ...normalized };
	} catch (e) {
		return { ok: false, items: [], nextCursor: '', msg: e instanceof Error ? e.message : String(e) };
	}
}

export async function fetchEventTypes(): Promise<EventTypeOption[]> {
	try {
		const res = await callInternal<EventTypeOption[]>({
			short_name: 'event',
			action: '/auth/event_type',
			method: 'GET',
			av: '1',
		});
		if (Number(res.code) !== 0 || !Array.isArray(res.data)) return [];
		return res.data;
	} catch {
		return [];
	}
}

export async function fetchEventSources(opts: { storeId?: string | number } = {}): Promise<EventTypeOption[]> {
	const query: Record<string, string | number> = {};
	if (opts.storeId) query.store_id = opts.storeId;
	return fetchEventOptionList<EventTypeOption>('/event/event_source', {
		query: Object.keys(query).length > 0 ? query : undefined,
	});
}

function uniqueEventStoreIds(ids: Array<string | number | null | undefined>): Array<string | number> {
	const result: Array<string | number> = [];
	for (const id of ids) {
		if (id === undefined || id === null || id === '') continue;
		let exists = false;
		for (const existing of result) {
			if (String(existing) === String(id)) {
				exists = true;
				break;
			}
		}
		if (!exists) result.push(id);
	}
	return result;
}

export async function fetchEventCreateTypes(opts: {
	storeId?: string | number;
	fallbackStoreIds?: Array<string | number | null | undefined>;
} = {}): Promise<EventTypeOption[]> {
	const versions = ['1', '2', '3'];
	const fallbackStoreIds = uniqueEventStoreIds(opts.fallbackStoreIds ?? []);
	if (opts.storeId) {
		const scoped = await fetchEventCreateTypeOptionList({
			query: { store_id: opts.storeId },
			versions,
		});
		if (scoped.length > 0) return scoped;
	}
	for (const fallbackStoreId of fallbackStoreIds) {
		if (opts.storeId && String(fallbackStoreId) === String(opts.storeId)) continue;
		const scoped = await fetchEventCreateTypeOptionList({
			query: { store_id: fallbackStoreId },
			versions,
		});
		if (scoped.length > 0) return scoped;
	}
	return fetchEventCreateTypeOptionList({ versions });
}

export async function fetchEventTemplates(opts: {
	storeId?: string | number;
	eventType?: string | number;
} = {}): Promise<EventTemplateOption[]> {
	const query: Record<string, string | number> = {};
	if (opts.storeId) query.store_id = opts.storeId;
	if (opts.eventType) query.event_type = opts.eventType;
	return fetchEventOptionList<EventTemplateOption>('/event/template', { query });
}

export async function fetchEventProperties(opts: {
	storeId?: string | number;
	eventType?: string | number;
	templateId?: string | number;
} = {}): Promise<EventPropertyOption[]> {
	const query: Record<string, string | number> = {};
	if (opts.storeId) query.store_id = opts.storeId;
	if (opts.eventType) query.event_type = opts.eventType;
	if (opts.templateId) query.template_id = opts.templateId;
	return fetchEventOptionList<EventPropertyOption>('/event/property_list', { query });
}

export async function fetchPriorityConfig(): Promise<EventPriorityOption[]> {
	for (const av of ['1', '2']) {
		try {
			const res = await callInternal<EventPriorityOption[] | { items?: EventPriorityOption[] }>({
				short_name: 'event',
				action: '/priority/config',
				method: 'GET',
				av,
			});
			if (Number(res.code) === 0) {
				return normalizeArray<EventPriorityOption>(res.data);
			}
		} catch {
			// Try the next observed internal API version.
		}
	}
	return [];
}

export async function fetchEventCreateUsersByStore(storeId: string | number): Promise<EventCreateUserGroup[]> {
	const groupId = cleanText(storeId);
	if (!groupId) return [];
	try {
		const res = await callInternal<unknown>({
			short_name: 'base',
			action: '/user/user_list_by_group',
			method: 'POST',
			body: { is_match: 0, group_id: groupId },
			av: '1',
		});
		if (Number(res.code) !== 0) return [];
		return normalizeEventCreateUserGroups(res.data);
	} catch {
		return [];
	}
}

export async function fetchEventDetail(eventId: string | number): Promise<{ ok: boolean; detail?: EventDetail; msg?: string }> {
	const attempts = [
		{ action: '/event/detail' },
		{ action: '/event/detail', av: '2' },
		{ action: '/auth/event_detail', av: '1' },
	];
	let lastMsg: string | undefined;
	for (const attempt of attempts) {
		try {
			const res = await callInternal<unknown>({
				short_name: 'event',
				action: attempt.action,
				method: 'GET',
				query: { event_id: eventId },
				...(attempt.av ? { av: attempt.av } : {}),
			});
			if (Number(res.code) === 0) {
				const detail = normalizeEventDetail(res.data);
				if (detail) return { ok: true, detail };
				lastMsg = 'Event detail response is empty';
				continue;
			}
			lastMsg = responseMsg(res.code, res.msg);
		} catch (e) {
			lastMsg = e instanceof Error ? e.message : String(e);
		}
	}
	return { ok: false, msg: lastMsg };
}

export async function addEventComment(opts: {
	eventId: string | number;
	content: string;
	userId?: string | number;
}): Promise<EventActionResult> {
	const attempts = [
		{ action: '/comment/add' },
		{ action: '/comment/add', av: '2' },
		{ action: '/auth/comment_add', av: '1' },
	];
	const body: Record<string, unknown> = { event_id: opts.eventId, content: opts.content };
	let lastMsg: string | undefined;
	for (const attempt of attempts) {
		try {
			const res = await callInternal({
				short_name: 'event',
				action: attempt.action,
				method: 'POST',
				body,
				...(attempt.av ? { av: attempt.av } : {}),
			});
			if (Number(res.code) === 0) return { ok: true, msg: res.msg };
			lastMsg = responseMsg(res.code, res.msg);
		} catch (e) {
			lastMsg = e instanceof Error ? e.message : String(e);
		}
	}
	return { ok: false, msg: lastMsg };
}

export async function lockEventForMe(opts: {
	eventId: string | number;
	userId?: string | number;
}): Promise<EventActionResult> {
	try {
		const query: Record<string, string | number> = { event_id: opts.eventId };
		if (opts.userId) query.user_id = opts.userId;
		const res = await callInternal({
			short_name: 'event',
			action: '/event/click_my_handle',
			method: 'GET',
			query,
			av: '2',
		});
		return { ok: Number(res.code) === 0, msg: Number(res.code) === 0 ? res.msg : responseMsg(res.code, res.msg) };
	} catch (e) {
		return { ok: false, msg: e instanceof Error ? e.message : String(e) };
	}
}

export async function auditEvent(opts: {
	eventId: string | number;
	auditId: string | number;
	auditResult: 0 | 1;
	userId?: string | number;
}): Promise<EventActionResult> {
	const attempts = [
		{ action: '/event/handle', av: '2' },
		{ action: '/auth/event_handle', av: '1' },
	];
	const query: Record<string, string | number> = {
		event_id: opts.eventId,
		audit_id: opts.auditId,
		audit_result: opts.auditResult,
	};
	if (opts.userId) query.user_id = opts.userId;
	let lastMsg: string | undefined;
	for (const attempt of attempts) {
		try {
			const res = await callInternal({
				short_name: 'event',
				action: attempt.action,
				method: 'GET',
				query,
				av: attempt.av,
			});
			if (Number(res.code) === 0) return { ok: true, msg: res.msg };
			lastMsg = responseMsg(res.code, res.msg);
		} catch (e) {
			lastMsg = e instanceof Error ? e.message : String(e);
		}
	}
	return { ok: false, msg: lastMsg };
}

export async function fetchAssignableUsers(opts: {
	eventId: string | number;
	userId: string | number;
}): Promise<{ ok: boolean; users: AssignableEventUser[]; msg?: string }> {
	try {
		const res = await callInternal<AssignableEventUser[]>({
			short_name: 'event',
			action: '/event/peers_and_lower_users',
			method: 'GET',
			query: { event_id: opts.eventId, user_id: opts.userId },
			av: '2',
		});
		if (Number(res.code) !== 0) return { ok: false, users: [], msg: responseMsg(res.code, res.msg) };
		return { ok: true, users: Array.isArray(res.data) ? res.data : [] };
	} catch (e) {
		return { ok: false, users: [], msg: e instanceof Error ? e.message : String(e) };
	}
}

export async function assignEvent(opts: {
	eventId: string | number;
	userIds: Array<string | number>;
}): Promise<EventActionResult> {
	try {
		const res = await callInternal({
			short_name: 'event',
			action: '/event/assign',
			method: 'POST',
			body: { event_id: opts.eventId, user_ids: opts.userIds.join(',') },
			av: '2',
		});
		return { ok: Number(res.code) === 0, msg: Number(res.code) === 0 ? res.msg : responseMsg(res.code, res.msg) };
	} catch (e) {
		return { ok: false, msg: e instanceof Error ? e.message : String(e) };
	}
}

function cleanEventCreateBody(payload: EventCreatePayload): Record<string, string | number> {
	const body: Record<string, string | number> = {};
	for (const [key, value] of Object.entries(payload)) {
		if (value === undefined || value === null || value === '') continue;
		body[key] = value as string | number;
	}
	return body;
}

export async function createEventInternal(payload: EventCreatePayload): Promise<EventActionResult & { eventId?: string | number }> {
	const body = cleanEventCreateBody(payload);
	try {
		const res = await callInternal<{ event_id?: string | number }>({
			short_name: 'event',
			action: '/event/add',
			method: 'POST',
			body,
			av: '2',
		});
		if (Number(res.code) === 0) return { ok: true, msg: res.msg, eventId: res.data?.event_id };
		return { ok: false, msg: responseMsg(res.code, res.msg) };
	} catch (firstError) {
		try {
			const res = await callInternal<{ event_id?: string | number }>({
				short_name: 'event',
				action: '/event/add_event',
				method: 'POST',
				body,
				av: '2',
			});
			if (Number(res.code) === 0) return { ok: true, msg: res.msg, eventId: res.data?.event_id };
			return { ok: false, msg: responseMsg(res.code, res.msg) };
		} catch (secondError) {
			return {
				ok: false,
				msg: secondError instanceof Error
					? secondError.message
					: firstError instanceof Error
						? firstError.message
						: String(secondError),
			};
		}
	}
}
